import { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Box,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Collapse,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { getDealValidationSchema } from "../../utils/validations";
import { DEAL_SOURCES, PRIORITY_LEVELS, DISPOSITION_OPTIONS, SUB_DISPOSITION_OPTIONS, REASON_FOR_CONTACT_OPTIONS, COMPLAINT_TYPE_OPTIONS, MACHINE_NUMBER_OPTIONS, ERROR_CODE_OPTIONS, RIDER_CLIENT_ID, RIDER_PLATFORM_OPTIONS, RIDER_STATUS_OPTIONS, TATWEER_CLIENT_ID, TATWEER_SERVICE_CATEGORY_OPTIONS, TATWEER_REQUEST_TYPE_OPTIONS, TATWEER_FOLLOW_UP_REQUEST } from "../../utils/constants";
import { formatDateForInput, getSessionRole } from "../../utils/helpers";
import axiosInstance from "../../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";
const COMPLAINT_STATUS_OPTIONS = [
  "Credit note promised",
  "Refund request",
  "Cx Received juice",
  "Credit note processed",
  "Refund processed",
  "Resolved"
];

const COMMAND_CODE_OPTIONS = [
  // "500",
  // "1000"
  "TOKEN"
];

export const DealForm = ({
  initialValues,
  onSubmit,
  companies,
  contacts,
  stages,
  onAddCompany,
  onAddContact,
  isSubmitting,
  submitLabel = "Create Lead",
  showActions = true,
  onCancel,
  onClose,
}) => {
  const [showComplaintDetails, setShowComplaintDetails] = useState(false);

  const userId = localStorage.getItem("userId");

  // "Assign to" (create only): team members a lead can be assigned to.
  // A team member creating a lead starts with themselves selected; the owner
  // starts with auto-assign (round-robin rules).
  const isCreate = !initialValues;
  const storedMemberId = String(localStorage.getItem("memberId") || "");
  const myMemberId = getSessionRole() !== "owner" && /^[a-f0-9]{24}$/i.test(storedMemberId) ? storedMemberId : "";
  const [teamMembers, setTeamMembers] = useState([]);
  useEffect(() => {
    if (!isCreate || !userId) return;
    axiosInstance
      .get(`${apiEndPointsConfig.getTeamMembersByClientId}/${userId}`)
      .then((res) =>
        setTeamMembers((res.data?.data || []).filter((m) => m.role !== "viewer" && m.isActive !== false))
      )
      .catch((err) => console.error("Error fetching team members:", err));
  }, [isCreate, userId]);

  const memberLabel = (id) => {
    if (!id) return myMemberId ? "Me" : "Auto-assign (round-robin)";
    const m = teamMembers.find((tm) => tm._id === id);
    const name = m?.name || [m?.firstName, m?.lastName].filter(Boolean).join(" ") || "Team member";
    return id === myMemberId ? `${name} (me)` : name;
  };

  const renderAssignField = (values, setFieldValue, label = "Assign to") =>
    isCreate && (
      <Autocomplete
        disableClearable
        // Team members: themselves first, then colleagues. Owner: auto-assign first.
        options={
          myMemberId
            ? [myMemberId, ...teamMembers.map((m) => m._id).filter((id) => id !== myMemberId)]
            : ["", ...teamMembers.map((m) => m._id)]
        }
        value={values.assignee || ""}
        onChange={(_, id) => setFieldValue("assignee", id || "")}
        getOptionLabel={memberLabel}
        isOptionEqualToValue={(option, value) => option === value}
        renderOption={(props, id) => {
          const m = teamMembers.find((tm) => tm._id === id);
          return (
            <li {...props} key={id || "auto"}>
              <Box>
                <Typography fontSize={14} fontWeight={600}>{memberLabel(id)}</Typography>
                <Typography fontSize={12} color="text.secondary">
                  {id ? m?.role || "" : myMemberId ? "Assign the lead to yourself" : "Use your auto-assign settings"}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": { minHeight: "44px", borderRadius: "12px" },
            }}
          />
        )}
      />
    );

  const defaultValues = {
    dealName: "",
    company: "",
    contactName: "",
    amount: "",
    closingDate: "",
    stage: stages[0]?._id || "",
    priority: "medium",
    email: "",
    source: "",
    description: "",
    mobile: "",
    country: "",
    jobTitle: "",
    linkedinUrl: "",
    primaryIndustry: "",
    interest: "",
    subInterest: "",
    disposition: "",
    subDisposition: "",
    whatsappSent: false,
    remarks: "",
    followUpDate: "",
    // Complaint Fields
    complaintDate: "",
    complaintTime: "",
    loggedBy: "",
    callerName: "",
    paymentType: "",
    machineNumber: "",
    reasonForContact: "",
    complaintType: "",
    errorCode: "",
    juicesPushed: "",
    creditNoteJuices: "",
    commandCode: "",
    complaintStatus: "",
    refundAmount: "",
    customerEmailAddress: "",
    complaintComments: "",
    whatsappNumber: "",
    assignee: myMemberId,
  };

  const transformedInitialValues = initialValues
    ? {
      dealName: initialValues.dealName || "",
      company:
        initialValues.companyName ||
        (typeof initialValues.company === "string"
          ? initialValues.company
          : initialValues.company?.name || ""),
      contactName:
        typeof initialValues.contactName === "string"
          ? initialValues.contactName
          : initialValues.contact
            ? `${initialValues.contact.firstName} ${initialValues.contact.lastName}`
            : "",
      amount: initialValues.amount || "",
      closingDate: initialValues.closingDate || "",
      stage:
        typeof initialValues.stage === "string"
          ? initialValues.stage
          : initialValues.stage?._id || "",
      priority: initialValues.priority || "medium",
      source: initialValues.source || "",
      description: initialValues.description || "",
      mobile: initialValues.mobile || "",
      email: initialValues.email || "",
      jobTitle: initialValues.jobTitle || "",
      linkedinUrl: initialValues.linkedinUrl || "",
      primaryIndustry: initialValues.primaryIndustry || "",
      id: initialValues._id || "",
      interest: initialValues.interest || "",
      subInterest: initialValues.subInterest || "",
      disposition: initialValues.disposition || "",
      subDisposition: initialValues.subDisposition || "",
      whatsappSent: initialValues.whatsappSent || false,
      remarks: initialValues.remarks || "",
      followUpDate: initialValues.followUpDate || "",
      city: initialValues.city || "",
      country: initialValues.country || "",
      // Complaint Fields
      complaintDate: initialValues.complaintDate || "",
      complaintTime: initialValues.complaintTime || "",
      loggedBy: initialValues.loggedBy || "",
      callerName: initialValues.callerName || "",
      paymentType: initialValues.paymentType || "",
      machineNumber: initialValues.machineNumber || "",
      reasonForContact: initialValues.reasonForContact || "",
      complaintType: initialValues.complaintType || "",
      errorCode: initialValues.errorCode || "",
      juicesPushed: initialValues.juicesPushed || "",
      creditNoteJuices: initialValues.creditNoteJuices || "",
      commandCode: initialValues.commandCode || "",
      complaintStatus: initialValues.complaintStatus || "",
      refundAmount: initialValues.refundAmount || "",
      customerEmailAddress: initialValues.customerEmailAddress || "",
      complaintComments: initialValues.complaintComments || "",
      whatsappNumber: initialValues.whatsappNumber || "",
    }
    : defaultValues;

  // ── Rider client (Gazmin) ────────────────────────────────────────────────
  // This client onboards delivery riders rather than customers, so it gets a
  // dedicated form with only the rider fields instead of the standard lead form.
  if (userId === RIDER_CLIENT_ID) {
    const riderInitialValues = {
      riderId: initialValues?.riderId || "",
      riderName: initialValues?.riderName || "",
      riderPlatform: initialValues?.riderPlatform || "",
      mobile: initialValues?.mobile || "",
      bikeNo: initialValues?.bikeNo || "",
      visaNo: initialValues?.visaNo || "",
      currentStore: initialValues?.currentStore || "",
      previousStore: initialValues?.previousStore || "",
      description: initialValues?.description || "",
      riderStatus: initialValues?.riderStatus || "",
      priority: initialValues?.priority || "medium",
      assignee: myMemberId,
      stage:
        (typeof initialValues?.stage === "string"
          ? initialValues.stage
          : initialValues?.stage?._id) ||
        stages[0]?._id ||
        "",
      id: initialValues?._id || "",
    };

    const fieldSx = {
      borderRadius: "12px",
      "& .MuiInputBase-root": {
        height: "44px",
        borderRadius: "12px",
      },
    };

    return (
      <Formik
        initialValues={riderInitialValues}
        validationSchema={getDealValidationSchema(userId)}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting: formSubmitting,
          setFieldValue,
        }) => (
          <Form>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="riderId"
                  placeholder="Rider ID *"
                  value={values.riderId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.riderId && Boolean(errors.riderId)}
                  helperText={touched.riderId && errors.riderId}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="riderName"
                  placeholder="Rider Name *"
                  value={values.riderName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.riderName && Boolean(errors.riderName)}
                  helperText={touched.riderName && errors.riderName}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={touched.riderPlatform && Boolean(errors.riderPlatform)}
                >
                  <InputLabel>Platform *</InputLabel>
                  <Select
                    name="riderPlatform"
                    value={values.riderPlatform}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Platform *"
                    sx={{ borderRadius: "12px", height: "44px" }}
                  >
                    {RIDER_PLATFORM_OPTIONS.map((platform) => (
                      <MenuItem key={platform} value={platform}>
                        {platform}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.riderPlatform && errors.riderPlatform && (
                    <FormHelperText>{errors.riderPlatform}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="mobile"
                  placeholder="Mobile No"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && Boolean(errors.mobile)}
                  helperText={touched.mobile && errors.mobile}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="bikeNo"
                  placeholder="Bike No"
                  value={values.bikeNo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="visaNo"
                  placeholder="Visa No"
                  value={values.visaNo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="currentStore"
                  placeholder="Current Store"
                  value={values.currentStore}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="previousStore"
                  placeholder="Previous Store"
                  value={values.previousStore}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="riderStatus"
                    value={values.riderStatus}
                    onChange={handleChange}
                    label="Status"
                    sx={{ borderRadius: "12px", height: "44px" }}
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    {RIDER_STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Stage</InputLabel>
                  <Select
                    name="stage"
                    value={values.stage}
                    onChange={handleChange}
                    label="Stage"
                    sx={{ borderRadius: "12px", height: "44px" }}
                  >
                    {stages.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    name="priority"
                    value={values.priority}
                    onChange={handleChange}
                    label="Priority"
                    sx={{ borderRadius: "12px", height: "44px" }}
                  >
                    {PRIORITY_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              name="description"
              placeholder="Description"
              multiline
              rows={3}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              sx={{
                mb: 2,
                borderRadius: "12px",
                "& .MuiInputBase-root": { borderRadius: "12px" },
              }}
            />

            {renderAssignField(values, setFieldValue)}

            {showActions && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  pt: 2,
                  borderTop: onCancel ? "1px solid #E5E7EB" : "none",
                }}
              >
                {onCancel && (
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={isSubmitting || formSubmitting}
                    sx={{ borderRadius: "12px", textTransform: "capitalize" }}
                    onClick={() => {
                      onCancel?.();
                      onClose();
                    }}
                  >
                    Cancel
                  </Button>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={isSubmitting || formSubmitting}
                  sx={{
                    bgcolor: "#6366f1",
                    borderRadius: "12px",
                    textTransform: "capitalize",
                    mb: 1,
                  }}
                >
                  {isSubmitting || formSubmitting ? "Saving..." : submitLabel}
                </Button>
              </Box>
            )}
          </Form>
        )}
      </Formik>
    );
  }

  // ── Tatweer client (facility services) ───────────────────────────────────
  // Leads are service requests (AC, plumbing, cleaning…), so this client gets a
  // dedicated form. Existing deal fields are reused where they fit: customer name
  // → dealName, issue → description, charges → amount, technician → assignee,
  // status → stage.
  if (userId === TATWEER_CLIENT_ID) {
    const tatweerInitialValues = {
      dealName: initialValues?.dealName || "",
      mobile: initialValues?.mobile || "",
      area: initialValues?.area || "",
      buildingName: initialValues?.buildingName || "",
      unitNo: initialValues?.unitNo || "",
      priority: initialValues?.priority || "medium",
      serviceCategory: initialValues?.serviceCategory || "",
      requestType: initialValues?.requestType || "",
      description: initialValues?.description || "",
      existingTicketId: initialValues?.existingTicketId || "",
      preferredDateTime: initialValues?.preferredDateTime || "",
      amount: initialValues?.amount ?? "",
      remarks: initialValues?.remarks || "",
      assignee: myMemberId,
      stage:
        (typeof initialValues?.stage === "string"
          ? initialValues.stage
          : initialValues?.stage?._id) ||
        stages[0]?._id ||
        "",
      id: initialValues?._id || "",
    };

    const fieldSx = {
      borderRadius: "12px",
      "& .MuiInputBase-root": {
        height: "44px",
        borderRadius: "12px",
      },
    };

    const sectionTitleSx = { fontWeight: 700, color: "#0f172a", mb: 1.5 };

    return (
      <Formik
        initialValues={tatweerInitialValues}
        validationSchema={getDealValidationSchema(userId)}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting: formSubmitting,
          setFieldValue,
        }) => {
          const textField = (name, placeholder, props = {}) => (
            <TextField
              fullWidth
              name={name}
              placeholder={placeholder}
              value={values[name]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched[name] && Boolean(errors[name])}
              helperText={touched[name] && errors[name]}
              sx={fieldSx}
              {...props}
            />
          );

          const selectField = (name, label, options, helperText) => (
            <FormControl fullWidth error={touched[name] && Boolean(errors[name])}>
              <InputLabel>{label}</InputLabel>
              <Select
                name={name}
                value={values[name]}
                onChange={handleChange}
                onBlur={handleBlur}
                label={label}
                sx={{ borderRadius: "12px", height: "44px" }}
              >
                {options.map((o) => (
                  <MenuItem key={o.value ?? o} value={o.value ?? o}>
                    {o.label ?? o}
                  </MenuItem>
                ))}
              </Select>
              {((touched[name] && errors[name]) || helperText) && (
                <FormHelperText>{(touched[name] && errors[name]) || helperText}</FormHelperText>
              )}
            </FormControl>
          );

          const isFollowUp = values.requestType === TATWEER_FOLLOW_UP_REQUEST;

          return (
            <Form>
              <Typography variant="subtitle2" sx={sectionTitleSx}>
                Customer Details
              </Typography>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  {textField("dealName", "Customer Name *")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {textField("mobile", "Mobile No *")}
                </Grid>
              </Grid>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  {textField("area", "Area / Community * (e.g. Al Nahda, JVC)")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {textField("buildingName", "Building Name")}
                </Grid>
              </Grid>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  {textField("unitNo", "Apartment / Villa No")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {selectField(
                    "priority",
                    "Priority",
                    PRIORITY_LEVELS.map((level) => ({
                      value: level,
                      label: level.charAt(0).toUpperCase() + level.slice(1),
                    })),
                    "Use High for leakage or electrical issues"
                  )}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ ...sectionTitleSx, mt: 1 }}>
                Service Details
              </Typography>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  {selectField("serviceCategory", "Service Category *", TATWEER_SERVICE_CATEGORY_OPTIONS)}
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.requestType && Boolean(errors.requestType)}>
                    <InputLabel>Request Type *</InputLabel>
                    <Select
                      name="requestType"
                      value={values.requestType}
                      onChange={(e) => {
                        setFieldValue("requestType", e.target.value);
                        // Ticket ID only applies to follow-ups
                        if (e.target.value !== TATWEER_FOLLOW_UP_REQUEST) {
                          setFieldValue("existingTicketId", "");
                        }
                      }}
                      onBlur={handleBlur}
                      label="Request Type *"
                      sx={{ borderRadius: "12px", height: "44px" }}
                    >
                      {TATWEER_REQUEST_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.requestType && errors.requestType && (
                      <FormHelperText>{errors.requestType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {textField("description", "Issue Description * (what the customer reported)", {
                multiline: true,
                rows: 3,
                sx: { mb: 2, borderRadius: "12px", "& .MuiInputBase-root": { borderRadius: "12px" } },
              })}

              <Grid container spacing={2} mb={2}>
                {isFollowUp && (
                  <Grid item xs={12} md={6}>
                    {textField("existingTicketId", "Existing Ticket ID *")}
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  {textField("preferredDateTime", "", {
                    type: "datetime-local",
                    label: "Preferred Date & Time",
                    InputLabelProps: { shrink: true },
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {textField("amount", "Charges (AED)", {
                    type: "number",
                    inputProps: { min: 0, step: "0.01" },
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {selectField(
                    "stage",
                    "Status",
                    stages.map((s) => ({ value: s._id, label: s.name }))
                  )}
                </Grid>
              </Grid>

              {renderAssignField(values, setFieldValue, "Assigned Technician")}

              {textField("remarks", "Remarks", {
                multiline: true,
                rows: 2,
                sx: { mb: 2, borderRadius: "12px", "& .MuiInputBase-root": { borderRadius: "12px" } },
              })}

              {showActions && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    pt: 2,
                    borderTop: onCancel ? "1px solid #E5E7EB" : "none",
                  }}
                >
                  {onCancel && (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={isSubmitting || formSubmitting}
                      sx={{ borderRadius: "12px", textTransform: "capitalize" }}
                      onClick={() => {
                        onCancel?.();
                        onClose();
                      }}
                    >
                      Cancel
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    type="submit"
                    disabled={isSubmitting || formSubmitting}
                    sx={{
                      bgcolor: "#6366f1",
                      borderRadius: "12px",
                      textTransform: "capitalize",
                      mb: 1,
                    }}
                  >
                    {isSubmitting || formSubmitting ? "Saving..." : submitLabel}
                  </Button>
                </Box>
              )}
            </Form>
          );
        }}
      </Formik>
    );
  }

  return (
    <Formik
      initialValues={transformedInitialValues}
      validationSchema={getDealValidationSchema(userId)}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting: formSubmitting,
        setFieldValue,
      }) => (
        <Form>
          {/* VALIDATION ONLY HERE */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="dealName"
                placeholder="Lead Name"
                value={values.dealName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dealName && Boolean(errors.dealName)}
                helperText={touched.dealName && errors.dealName}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="mobile"
                placeholder="Mobile No*"
                value={values.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.mobile && Boolean(errors.mobile)}
                helperText={touched.mobile && errors.mobile}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="company"
                placeholder="Company Name"
                value={values.company}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="closingDate"
                placeholder="Closing Date"
                type="date"
                value={
                  values.closingDate
                    ? formatDateForInput(values.closingDate)
                    : ""
                }
                onChange={handleChange}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="jobTitle"
                placeholder="Job Title"
                value={values.jobTitle || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="primaryIndustry"
                placeholder="Primary Industry"
                value={values.primaryIndustry || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="country"
                placeholder="Country (e.g. UAE)"
                value={values.country || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="whatsappNumber"
                placeholder="WhatsApp Number"
                value={values.whatsappNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                name="linkedinUrl"
                placeholder="LinkedIn Profile URL (e.g. https://linkedin.com/in/...)"
                value={values.linkedinUrl || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="email"
                placeholder="Email Address"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={values.priority}
                  onChange={handleChange}
                  label="Priority"
                  sx={{
                    borderRadius: "12px",
                    height: "44px",
                  }}
                >
                  {PRIORITY_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>



          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                name="source"
                value={
                  DEAL_SOURCES.find((source) => source === values.source) || ""
                }
                onChange={(event, newValue) => {
                  setFieldValue("source", newValue || "");
                }}
                options={DEAL_SOURCES}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Deal Source"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiInputBase-root": {
                        height: "44px",
                        borderRadius: "12px",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="city"
                placeholder="Location/City"
                value={values.city || ""}
                onChange={handleChange}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          {renderAssignField(values, setFieldValue)}

          <TextField
            fullWidth
            name="description"
            placeholder="Description"
            // multiline
            // rows={3}
            value={values.description}
            onChange={handleChange}
            sx={{
              mb: 2,
              borderRadius: "12px",
              "& .MuiInputBase-root": {
                borderRadius: "12px",
              },
            }}
          />

          {userId === "69afdc065323c68fd76cdabf" && (
            <>
              <Box
                onClick={() => setShowComplaintDetails(!showComplaintDetails)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  p: '12px 16px',
                  mt: 2.5,
                  mb: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: showComplaintDetails ? '#6366f1' : '#e2e8f0',
                  bgcolor: showComplaintDetails ? '#f8fafc' : '#ffffff',
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  '&:hover': {
                    borderColor: '#6366f1',
                    bgcolor: '#f8fafc',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>🚨</Typography>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: showComplaintDetails ? '#6366f1' : '#0f172a', lineHeight: 1.2 }}>
                      Complaint & Ticket Details
                    </Typography>
                    {/* <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                      Optional details for customer complaints or machine errors
                    </Typography> */}
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                  {showComplaintDetails ? 'Hide Section ✕' : 'Show Section ＋'}
                </Typography>
              </Box>

              <Collapse in={showComplaintDetails} sx={{ width: '100%' }}>
                <Box sx={{ pt: 1 }}>


                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={MACHINE_NUMBER_OPTIONS}
                        value={values.machineNumber || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("machineNumber", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Machine ID *"
                            error={touched.machineNumber && Boolean(errors.machineNumber)}
                            helperText={touched.machineNumber && errors.machineNumber}
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Type</InputLabel>
                        <Select
                          name="paymentType"
                          value={values.paymentType}
                          onChange={handleChange}
                          label="Payment Type"
                          sx={{
                            borderRadius: "12px",
                            height: "44px",
                          }}
                        >
                          <MenuItem value="">Select Payment Type</MenuItem>
                          <MenuItem value="Cash">Cash</MenuItem>
                          <MenuItem value="Debit Card">Debit Card</MenuItem>
                          <MenuItem value="Credit Card">Credit Card</MenuItem>
                          <MenuItem value="Wallet">Wallet</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={REASON_FOR_CONTACT_OPTIONS}
                        value={values.reasonForContact || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("reasonForContact", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Reason For Contact"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={COMPLAINT_TYPE_OPTIONS}
                        value={values.complaintType || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("complaintType", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Complaint Type"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={ERROR_CODE_OPTIONS}
                        value={values.errorCode || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("errorCode", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Error Code"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="refundAmount"
                        placeholder="Refunds (AED)"
                        value={values.refundAmount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="juicesPushed"
                        placeholder="Juices Pushed Count"
                        value={values.juicesPushed}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="creditNoteJuices"
                        placeholder="Credit Note Juices Count"
                        value={values.creditNoteJuices}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={COMMAND_CODE_OPTIONS}
                        value={values.commandCode || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("commandCode", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Command Code"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="customerEmailAddress"
                        placeholder="Customer Email Address"
                        value={values.customerEmailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={12}>
                      <Autocomplete
                        fullWidth
                        options={COMPLAINT_STATUS_OPTIONS}
                        value={values.complaintStatus || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("complaintStatus", newValue || "");
                          if (stages && stages.length > 0) {
                            const matchedStage = stages.find(
                              (s) => s.name.toLowerCase().trim() === (newValue || "").toLowerCase().trim()
                            );
                            if (matchedStage) {
                              setFieldValue("stage", matchedStage._id);
                            } else {
                              setFieldValue("stage", stages[0]._id);
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Status *"
                            error={touched.complaintStatus && Boolean(errors.complaintStatus)}
                            helperText={touched.complaintStatus && errors.complaintStatus}
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>


                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      name="complaintComments"
                      placeholder="Complaint Comments"
                      multiline
                      rows={3}
                      value={values.complaintComments}
                      onChange={handleChange}
                      sx={{
                        // mb: 2.5,
                        borderRadius: "12px",
                        "& .MuiInputBase-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>

                </Box>
              </Collapse>
            </>
          )}
          {showActions && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                pt: 2,
                borderTop: onCancel ? "1px solid #E5E7EB" : "none",
              }}
            >
              {onCancel && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={isSubmitting || formSubmitting}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "capitalize",
                  }}
                  onClick={() => {
                    onCancel?.();
                    onClose();
                  }}
                >
                  Cancel
                </Button>
              )}

              <Button
                variant="contained"
                fullWidth
                type="submit"
                disabled={isSubmitting || formSubmitting}
                sx={{
                  bgcolor: "#6366f1",
                  borderRadius: "12px",
                  textTransform: "capitalize",
                  mb: 1,
                }}
              >
                {isSubmitting || formSubmitting ? "Saving..." : submitLabel}
              </Button>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};
